import React, { useEffect, useState, useMemo } from 'react';
import { useToasts } from 'react-toast-notifications';
import { Row, Col, Button, Input, Card, CardBody, CardFooter, FormGroup, Table, 
    Modal, ModalHeader, ModalBody, ModalFooter, Form, Label, Badge, InputGroup } from 'reactstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withTranslation } from 'react-multi-lang';
import {
    faPen,
    faPlus,
    faRefresh,
    faKey,
    faTimes
} from "@fortawesome/free-solid-svg-icons";
import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import { saveAs } from 'file-saver';
import UsersService from '../Services/UsersService';
import EssService from '../Services/EssService';
import AuthService from '../Services/AuthService';

export const COMPANY_OPTIONS = ['NORDIA', 'NORDIA ENERGY', 'ΜΑΡΜΑΡΑ ΔΙΟΝΥΣΟΥ'];

export const SPECIALTY_OPTIONS = [
    'ΑΞΙΟΛΟΓΙΤΗΣ',
    'ΑΠΟΘΗΚΗ',
    'ΓΟΜΩΤΗΣ',
    'ΔΙΕΥΘΥΝΤΗΣ ΤΟΜΕΑ',
    'ΕΜΠΟΡΙΚΟΣ Δ/ΝΤΗΣ',
    'ΕΡΓΟΔΗΓΟΣ',
    'ΚΑΘΑΡΙΣΤΡΙΑ',
    'ΛΟΓΙΣΤΗΡΙΟ',
    'ΜΑΡΚΕΤΙΝΓΚ',
    'ΜΗΧΑΝΟΓΡΑΦΙΣΗ',
    'ΟΔΗΓΟΣ',
    'ΠΑΡΑΓΩΓΗ',
    'ΠΡΟΙΣΤΑΜΕΝΟΣ ΕΡΓΟΥ',
    'ΠΡΟΜΗΘΕΙΕΣ',
    'ΠΩΛΗΤΗΣ',
    'ΣΥΝΤΗΡΗΣΗ',
    'ΣΥΡΜΑΤΟΚΟΠΗ',
    'ΤΕΧΝΙΚΗ ΥΠΟΣΤΗΡΙΞΗ',
    'ΥΠΑΛΛΗΛΟΣ ΓΡΑΦΕΙΟΥ',
    'ΧΕΙΡΙΣΤΗΣ ΓΕΡΑΝΟΓΕΦΥΡΑΣ',
    'ΧΕΙΡΙΣΤΗΣ ΚΛΑΡΚ',
    'ΧΕΙΡΙΣΤΗΣ Μ.Ε.',
    'ΧΗΜΕΙΟ'
];

const Users = ({ t }) => {
    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [managerFilter, setManagerFilter] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const [user, setUser] = useState({
        username: '',
        password: '',
        name: '',
        role: 'weigher',
        whs: '',
        specialty: '',
        company: '',
        manager: '',
        hireYear: '',
        hireMonth: '',
        mustChangePassword: false
    });
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [passwordData, setPasswordData] = useState({
        userId: null,
        newPassword: '',
        confirmPassword: '',
        mustChangePassword: false
    });
    const [specialties, setSpecialties] = useState([]);

    const toggleUserModal = () => {
        if (userModalOpen) {
            setUser({
                username: '',
                password: '',
                name: '',
                role: 'weigher',
                whs: '',
                specialty: '',
                company: '',
                manager: '',
                hireYear: '',
                hireMonth: '',
                mustChangePassword: false
            });
            setIsEdit(false);
        }
        setUserModalOpen(!userModalOpen);
    }

    const togglePasswordModal = () => {
        if (passwordModalOpen) {
            setPasswordData({
                userId: null,
                newPassword: '',
                confirmPassword: '',
                mustChangePassword: false
            });
        }
        setPasswordModalOpen(!passwordModalOpen);
    }

    const usersService = useMemo(() => new UsersService(), []);
    const essService = useMemo(() => new EssService(), []);
    const authService = useMemo(() => new AuthService(), []);
    const [currentUser, setCurrentUser] = useState(authService.getUser() || {});
    const isEkolaitis = (currentUser?.username || '').toLowerCase() === 'ekolaitis';
    const [syncing, setSyncing] = useState(false);
    const { addToast } = useToasts();

    const roles = [
        "weigher", "assessor", "salesman", "admin", "manager", "quarryManager", "accountant", "salesmanager", "operator", "hrManager", "hrManagement", "phoneCenter", "IT"
    ];

    const r1 = (currentUser?.role || '').toLowerCase();
    const r2 = (currentUser?.role2 || '').toLowerCase();
    const isAdminOrHr = ['admin', 'hrmanager', 'hrmanagement'].includes(r1) || ['admin', 'hrmanager', 'hrmanagement'].includes(r2);
    const isIT = (r1 === 'it' || r2 === 'it') && !isAdminOrHr;

    const loadAllUsers = () => {
        usersService.getUsers().then(r => {
            setAllUsers(r || []);
        }).catch(e => console.error(e));
    };

    const fetchUsers = (currentFilters) => {
        const filtersToApply = currentFilters !== undefined ? currentFilters : {
            search,
            role: roleFilter,
            manager: managerFilter
        };
        usersService.getUsers(filtersToApply).then(r => {
            setUsers(r || []);
        }).catch(e => addToast(e, { appearance: 'error', autoDismiss: true }));
    };

    const getSpecialties = () => {
        essService.getSpecialties()
            .then(res => {
                const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
                setSpecialties(list);
            })
            .catch(e => {
                console.error('Error loading specialties:', e);
            });
    };

    useEffect(() => {
        loadAllUsers();
        getSpecialties();
    }, []);

    useEffect(() => {
        setPage(1);
        fetchUsers({
            search,
            role: roleFilter,
            manager: managerFilter
        });
    }, [search, roleFilter, managerFilter]);

    useEffect(() => {
        setPage(1);
    }, [perPage]);

    const refreshUsers = () => {
        loadAllUsers();
        fetchUsers({
            search,
            role: roleFilter,
            manager: managerFilter
        });
    };

    const handleSyncEmployees = () => {
        if (syncing) return;
        setSyncing(true);
        usersService.syncEmployees()
            .then(res => {
                addToast(
                    `Employees synchronized! (Updated: ${res.updated || 0}, Created: ${res.created || 0}, Unchanged: ${res.unchanged || 0})`,
                    { appearance: 'success', autoDismiss: true }
                );

                // Export and download Excel file with all user names, usernames, and passwords
                if (res?.excelBase64) {
                    try {
                        const binaryStr = window.atob(res.excelBase64);
                        const bytes = new Uint8Array(binaryStr.length);
                        for (let i = 0; i < binaryStr.length; i++) {
                            bytes[i] = binaryStr.charCodeAt(i);
                        }
                        const blob = new Blob([bytes], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        });
                        saveAs(blob, 'users_credentials.xlsx');
                        addToast('Downloaded users credentials Excel file.', { appearance: 'info', autoDismiss: true });
                    } catch (dlErr) {
                        console.error('Failed to download credentials Excel:', dlErr);
                    }
                }

                refreshUsers();
            })
            .catch(err => {
                addToast(err || 'Failed to sync employees', { appearance: 'error', autoDismiss: true });
            })
            .finally(() => {
                setSyncing(false);
            });
    };

    const getUsers = refreshUsers;

    const clearFilters = () => {
        setSearch('');
        setRoleFilter('');
        setManagerFilter('');
        setPage(1);
    };

    const isValidPassword = (password) => {
        if (!password || password.length < 8) return false;
        return /[a-z]/.test(password) && /[A-Z]/.test(password) && /[0-9]/.test(password);
    };

    const saveUser = (e) => {
        e.preventDefault();
        if (!isEdit && !isValidPassword(user.password)) {
            addToast(t('users.passwordComplexityError') || 'Ο κωδικός πρέπει να αποτελείται από τουλάχιστον 8 χαρακτήρες και να περιέχει τουλάχιστον ένα πεζό, ένα κεφαλαίο γράμμα και έναν αριθμό', { appearance: 'error', autoDismiss: true });
            return;
        }
        if (isEdit && user.password && user.password.length > 0 && !isValidPassword(user.password)) {
            addToast(t('users.passwordComplexityError') || 'Ο κωδικός πρέπει να αποτελείται από τουλάχιστον 8 χαρακτήρες και να περιέχει τουλάχιστον ένα πεζό, ένα κεφαλαίο γράμμα και έναν αριθμό', { appearance: 'error', autoDismiss: true });
            return;
        }
        const serviceCall = isEdit ? usersService.updateUser(user) : usersService.createUser(user);
        
        serviceCall.then(r => {
            addToast(t('users.saveSuccess'), { appearance: 'success', autoDismiss: true });
            toggleUserModal();
            refreshUsers();
        }).catch(e => {
            addToast(e || t('users.saveError'), { appearance: 'error', autoDismiss: true });
        });
    };

    const openEditUser = (u) => {
        setUser({ ...u, password: '', mustChangePassword: !!u.mustChangePassword });
        setIsEdit(true);
        setUserModalOpen(true);
    }

    const openPasswordModal = (u) => {
        setPasswordData({
            userId: u.userId,
            newPassword: '',
            confirmPassword: '',
            mustChangePassword: !!u.mustChangePassword
        });
        setPasswordModalOpen(true);
    }

    const handleChangePassword = (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            addToast(t('users.passwordsDontMatch'), { appearance: 'error', autoDismiss: true });
            return;
        }

        if (!isValidPassword(passwordData.newPassword)) {
            addToast(t('users.passwordComplexityError') || 'Ο κωδικός πρέπει να αποτελείται από τουλάχιστον 8 χαρακτήρες και να περιέχει τουλάχιστον ένα πεζό, ένα κεφαλαίο γράμμα και έναν αριθμό', { appearance: 'error', autoDismiss: true });
            return;
        }

        usersService.changePassword(passwordData.userId, passwordData.newPassword, passwordData.mustChangePassword).then(r => {
            addToast(t('users.changePasswordSuccess'), { appearance: 'success', autoDismiss: true });
            togglePasswordModal();
            refreshUsers();
        }).catch(e => {
            addToast(e || t('users.saveError'), { appearance: 'error', autoDismiss: true });
        });
    }

    const isSameCompany = (targetUser) => {
        if (!currentUser?.company || !targetUser?.company) return false;
        return currentUser.company.trim().toLowerCase() === targetUser.company.trim().toLowerCase();
    };

    const baseUsersList = (allUsers.length > 0 ? allUsers : users).filter(u => !isIT || isSameCompany(u));

    const matchesSearch = (u, term) => {
        if (!term || term.trim() === '') return true;
        const s = term.trim().toLowerCase();
        return (
            (u.name && u.name.toLowerCase().includes(s)) ||
            (u.username && u.username.toLowerCase().includes(s)) ||
            (u.specialty && u.specialty.toLowerCase().includes(s)) ||
            (u.company && u.company.toLowerCase().includes(s)) ||
            (u.whs && String(u.whs).toLowerCase().includes(s)) ||
            (u.manager && u.manager.toLowerCase().includes(s)) ||
            (u.role && u.role.toLowerCase().includes(s)) ||
            (u.role2 && u.role2.toLowerCase().includes(s))
        );
    };

    const isUnderManager = (u, selectedManager) => {
        if (!selectedManager || selectedManager === 'all') return true;
        if (!u.manager) return false;
        const mgrLower = selectedManager.toLowerCase();
        if (u.manager.toLowerCase() === mgrLower) return true;
        const mgrUser = baseUsersList.find(au => 
            (au.name && au.name.toLowerCase() === mgrLower) || 
            (au.username && au.username.toLowerCase() === mgrLower)
        );
        if (mgrUser) {
            if (mgrUser.name && u.manager.toLowerCase() === mgrUser.name.toLowerCase()) return true;
            if (mgrUser.username && u.manager.toLowerCase() === mgrUser.username.toLowerCase()) return true;
        }
        return false;
    };

    const displayedUsers = useMemo(() => {
        return users.filter(u => {
            if (isIT && !isSameCompany(u)) return false;
            if (!matchesSearch(u, search)) return false;
            if (roleFilter && roleFilter !== 'all') {
                const rf = roleFilter.toLowerCase();
                const matchesRole = (u.role && u.role.toLowerCase() === rf) || (u.role2 && u.role2.toLowerCase() === rf);
                if (!matchesRole) return false;
            }
            if (managerFilter && managerFilter !== 'all') {
                if (!isUnderManager(u, managerFilter)) return false;
            }
            return true;
        });
    }, [users, search, roleFilter, managerFilter, baseUsersList, isIT, currentUser]);

    const totalEntries = displayedUsers.length;
    const totalPages = Math.max(1, Math.ceil(totalEntries / perPage));
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    const paginatedUsers = useMemo(() => {
        return displayedUsers.slice(startIndex, endIndex);
    }, [displayedUsers, startIndex, endIndex]);

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    const managerOptions = useMemo(() => {
        return baseUsersList.map(u => ({
            value: u.name || u.username,
            label: u.name ? `${u.name} (@${u.username})` : u.username,
            user: u
        }));
    }, [baseUsersList]);

    const managerFilterOptions = useMemo(() => {
        const options = [];
        const seen = new Set();

        managerOptions.forEach(opt => {
            if (opt.value && !seen.has(opt.value)) {
                seen.add(opt.value);
                options.push({ value: opt.value, label: opt.label });
            }
        });

        baseUsersList.forEach(u => {
            if (u.manager && !seen.has(u.manager)) {
                seen.add(u.manager);
                options.push({ value: u.manager, label: u.manager });
            }
        });

        return options;
    }, [managerOptions, baseUsersList]);

    const specialtyOptions = useMemo(() => {
        const set = new Set(SPECIALTY_OPTIONS);
        (specialties || []).forEach(s => {
            const name = (typeof s === 'string' ? s : s?.name || '').trim();
            if (name) set.add(name);
        });
        if (user.specialty && user.specialty.trim()) {
            set.add(user.specialty.trim());
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'el', { sensitivity: 'base' }));
    }, [specialties, user.specialty]);

    const selectedManagerOption = managerOptions.find(opt => 
        opt.value === user.manager || opt.user?.username === user.manager
    ) || (user.manager ? { value: user.manager, label: user.manager } : null);

    return (
        <Card style={{ padding: '20px' }}>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <h3 className="mb-0">{t('users.userManagement')}</h3>
                        <Badge color="info" pill className="ms-2 ml-2 px-2 py-1" id="totalEntriesBadge" style={{ fontSize: '0.85rem' }}>
                            {t('users.totalEntries') || 'Total Entries'}: {displayedUsers.length}
                        </Badge>
                    </div>
                    <div className="d-flex align-items-center">
                        {isEkolaitis && !isIT && (
                            <Button
                                id="syncEmployeesButton"
                                color="dark"
                                onClick={handleSyncEmployees}
                                disabled={syncing}
                                className="mr-2 me-2 text-nowrap"
                                title="Sync employees from Excel registry"
                            >
                                <FontAwesomeIcon icon={faRefresh} spin={syncing} className="me-1 mr-1" />
                                {syncing ? 'Syncing...' : 'Sync Employees'}
                            </Button>
                        )}
                        {!isIT && (
                            <Button color="primary" onClick={toggleUserModal} className="mr-2 me-2">
                                <FontAwesomeIcon icon={faPlus} /> {t('users.addUser')}
                            </Button>
                        )}
                        <ReactHTMLTableToExcel
                            id="usersExcelButton"
                            className="btn btn-success mr-2 me-2"
                            table="usersExportTable"
                            filename="users"
                            sheet="users"
                            buttonText="Excel"
                        />
                        <Button color="secondary" onClick={refreshUsers} title="Refresh">
                            <FontAwesomeIcon icon={faRefresh} />
                        </Button>
                    </div>
                </div>

                {/* Filter Controls */}
                <Row className="mb-3 g-2 align-items-center">
                    <Col md="4" sm="6" xs="12">
                        <InputGroup size="sm">
                            <Input
                                id="userSearchInput"
                                type="text"
                                placeholder={t('users.searchPlaceholder') || 'Search users...'}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            {search && (
                                <Button color="secondary" outline size="sm" onClick={() => setSearch('')}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                            )}
                        </InputGroup>
                    </Col>
                    <Col md="3" sm="6" xs="12">
                        <Input
                            id="roleFilterSelect"
                            type="select"
                            bsSize="sm"
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                        >
                            <option value="">{t('users.allRoles') || 'All Roles'}</option>
                            {roles.map(r => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </Input>
                    </Col>
                    <Col md="3" sm="6" xs="12">
                        <Input
                            id="managerFilterSelect"
                            type="select"
                            bsSize="sm"
                            value={managerFilter}
                            onChange={(e) => setManagerFilter(e.target.value)}
                        >
                            <option value="">{t('users.allManagers') || 'All Managers'}</option>
                            {managerFilterOptions.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </Input>
                    </Col>
                    <Col md="2" sm="6" xs="12" className="d-flex align-items-center justify-content-between">
                        {(search || roleFilter || managerFilter) ? (
                            <Button 
                                color="light" 
                                size="sm" 
                                onClick={clearFilters}
                                className="border me-2 mr-2 text-nowrap"
                            >
                                <FontAwesomeIcon icon={faTimes} className="me-1 mr-1" />
                                {t('users.clearFilters') || 'Clear'}
                            </Button>
                        ) : <div />}
                        <span className="text-muted small text-nowrap ms-auto ml-auto" id="totalEntriesCount">
                            {t('users.totalEntries') || 'Total Entries'}: <strong>{displayedUsers.length}</strong>
                        </span>
                    </Col>
                </Row>

                <Table responsive striped id="usersTable">
                        <thead className="table-light sticky-top" style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f8f9fa' }}>
                            <tr>
                                <th>{t('users.name')}</th>
                                <th>{t('users.username')}</th>
                                <th>{t('users.role')}</th>
                                <th>{t('users.role2')}</th>
                                <th>{t('users.whs')}</th>
                                <th>Specialty</th>
                                <th>Company</th>
                                <th>Manager</th>
                                <th>Hire Info</th>
                                <th>Leave Days</th>
                                <th>Status</th>
                                <th>{t('users.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                        {paginatedUsers.map(u => (
                            <tr key={u.userId}>
                                <td>
                                    {u.name}
                                </td>
                                <td>{u.username}</td>
                                <td>{u.role}</td>
                                <td>{u.role2}</td>
                                <td>{u.whs}</td>
                                <td>{u.specialty || '-'}</td>
                                <td>{u.company || '-'}</td>
                                <td>{u.manager || '-'}</td>
                                <td>{u.hireYear ? `${u.hireYear}/${u.hireMonth || 1}` : '-'}</td>
                                <td>{u.totalLeaveDays !== undefined ? u.totalLeaveDays : '-'}</td>
                                <td>
                                    {u.isDeactivated ? (
                                        <span className="badge bg-danger" style={{ fontSize: '0.75rem' }}>Deactivated</span>
                                    ) : (
                                        <span className="badge bg-success" style={{ fontSize: '0.75rem' }}>Active</span>
                                    )}
                                    {u.mustChangePassword && (
                                        <span className="badge bg-warning text-dark ms-1 ml-1" style={{ fontSize: '0.75rem' }} title={t('users.mustChangePassword') || 'Force password change at next login'}>
                                            Pwd Reset
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {!isIT && (
                                        <Button color="info" size="sm" onClick={() => openEditUser(u)} className="mr-1 me-1">
                                            <FontAwesomeIcon icon={faPen} />
                                        </Button>
                                    )}
                                    <Button color="warning" size="sm" onClick={() => openPasswordModal(u)} className="mr-1 me-1" title={t('users.changePassword') || 'Change Password'}>
                                        <FontAwesomeIcon icon={faKey} />
                                    </Button>
                                    {!isIT && (
                                        <Button 
                                            color={u.isDeactivated ? "success" : "danger"} 
                                            size="sm" 
                                            onClick={() => {
                                                const updated = { ...u, isDeactivated: !u.isDeactivated };
                                                usersService.updateUser(updated).then(() => {
                                                    addToast(u.isDeactivated ? "User activated" : "User deactivated", { appearance: 'success', autoDismiss: true });
                                                    refreshUsers();
                                                }).catch(e => addToast(e, { appearance: 'error', autoDismiss: true }));
                                            }}
                                        >
                                            {u.isDeactivated ? "Activate" : "Deactivate"}
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {displayedUsers.length === 0 && (
                            <tr>
                                <td colSpan="12" className="text-center text-muted py-4">
                                    {t('users.noUsersFound') || 'No users found'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                {/* Hidden Table for Complete Excel Export */}
                <Table id="usersExportTable" className="d-none">
                    <thead>
                        <tr>
                            <th>{t('users.name') || 'Name'}</th>
                            <th>{t('users.username') || 'Username'}</th>
                            <th>{t('users.role') || 'Role'}</th>
                            <th>{t('users.role2') || 'Role 2'}</th>
                            <th>{t('users.whs') || 'Warehouse'}</th>
                            <th>Specialty</th>
                            <th>Company</th>
                            <th>Manager</th>
                            <th>Hire Year</th>
                            <th>Hire Month</th>
                            <th>Hire Info</th>
                            <th>Leave Days</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedUsers.map(u => (
                            <tr key={`export-${u.userId}`}>
                                <td>{u.name || ''}</td>
                                <td>{u.username || ''}</td>
                                <td>{u.role || ''}</td>
                                <td>{u.role2 || ''}</td>
                                <td>{u.whs || ''}</td>
                                <td>{u.specialty || ''}</td>
                                <td>{u.company || ''}</td>
                                <td>{u.manager || ''}</td>
                                <td>{u.hireYear || ''}</td>
                                <td>{u.hireMonth || ''}</td>
                                <td>{u.hireYear ? `${u.hireYear}/${u.hireMonth || 1}` : ''}</td>
                                <td>{u.totalLeaveDays !== undefined ? u.totalLeaveDays : ''}</td>
                                <td>{u.isDeactivated ? 'Deactivated' : 'Active'}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </CardBody>

            <CardFooter className="d-flex flex-wrap justify-content-between align-items-center py-2 bg-white border-top">
                <div className="d-flex flex-wrap align-items-center my-1">
                    <span className="text-muted small me-3 mr-3" id="showingEntriesText">
                        {t('users.showing') || 'Showing'} {totalEntries === 0 ? 0 : startIndex + 1} - {Math.min(endIndex, totalEntries)} {t('users.of') || 'of'} {totalEntries}
                    </span>
                    <div className="d-flex align-items-center">
                        <Label for="perPageSelect" className="mb-0 me-2 mr-2 text-nowrap small text-muted">
                            {t('users.perPage') || 'Per page'}:
                        </Label>
                        <Input
                            type="select"
                            id="perPageSelect"
                            data-testid="perPageSelect"
                            bsSize="sm"
                            style={{ width: '80px', display: 'inline-block' }}
                            value={perPage.toString()}
                            onChange={(e) => {
                                setPerPage(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                        </Input>
                    </div>
                </div>

                <div className="d-flex align-items-center my-1" id="paginationControls" data-testid="paginationControls">
                    <Button 
                        color="secondary" 
                        outline 
                        size="sm" 
                        onClick={() => setPage(1)} 
                        disabled={currentPage <= 1} 
                        className="me-1 mr-1" 
                        title={t('users.first') || 'First'}
                        id="firstPageBtn"
                    >
                        «
                    </Button>
                    <Button 
                        color="secondary" 
                        outline 
                        size="sm" 
                        onClick={() => setPage(p => Math.max(1, p - 1))} 
                        disabled={currentPage <= 1} 
                        className="me-1 mr-1" 
                        title={t('users.previous') || 'Previous'}
                        id="prevPageBtn"
                    >
                        ‹
                    </Button>
                    {getPageNumbers().map(p => (
                        <Button
                            key={p}
                            color={p === currentPage ? "primary" : "secondary"}
                            outline={p !== currentPage}
                            size="sm"
                            onClick={() => setPage(p)}
                            className="me-1 mr-1"
                        >
                            {p}
                        </Button>
                    ))}
                    <Button 
                        color="secondary" 
                        outline 
                        size="sm" 
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                        disabled={currentPage >= totalPages} 
                        className="me-1 mr-1" 
                        title={t('users.next') || 'Next'}
                        id="nextPageBtn"
                    >
                        ›
                    </Button>
                    <Button 
                        color="secondary" 
                        outline 
                        size="sm" 
                        onClick={() => setPage(totalPages)} 
                        disabled={currentPage >= totalPages} 
                        title={t('users.last') || 'Last'}
                        id="lastPageBtn"
                    >
                        »
                    </Button>
                </div>
            </CardFooter>

            <Modal isOpen={userModalOpen} toggle={toggleUserModal}>
                <Form onSubmit={saveUser}>
                    <ModalHeader toggle={toggleUserModal}>
                        {isEdit ? t('users.editUser') : t('users.addUser')}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>{t('users.name')}</Label>
                            <Input 
                                type="text" 
                                required
                                value={user.name} 
                                onChange={(e) => setUser({ ...user, name: e.target.value })} 
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('users.username')}</Label>
                            <Input 
                                type="text" 
                                required
                                value={user.username} 
                                onChange={(e) => setUser({ ...user, username: e.target.value })} 
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('users.password')}</Label>
                            <Input 
                                type="password" 
                                required={!isEdit}
                                placeholder={isEdit ? t('users.passwordHelp') : ''}
                                value={user.password} 
                                onChange={(e) => setUser({ ...user, password: e.target.value })} 
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('users.role')}</Label>
                            <Input 
                                type="select" 
                                value={user.role} 
                                onChange={(e) => setUser({ ...user, role: e.target.value })}
                            >
                                {roles.map(r => <option key={r} value={r}>{r}</option>)}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('users.role2')}</Label>
                            <Input 
                                type="select" 
                                value={user.role2 || ''} 
                                onChange={(e) => setUser({ ...user, role2: e.target.value })}
                            >
                                <option value="">-</option>
                                {roles.map(r => <option key={r} value={r}>{r}</option>)}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('users.whs')}</Label>
                            <Input 
                                type="text" 
                                value={user.whs || ''} 
                                onChange={(e) => setUser({ ...user, whs: e.target.value })} 
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Specialty</Label>
                            <Input 
                                id="userSpecialtySelect"
                                type="select" 
                                value={user.specialty || ''} 
                                onChange={(e) => setUser({ ...user, specialty: e.target.value })}
                            >
                                <option value="">-</option>
                                {specialtyOptions.map(specName => (
                                    <option key={specName} value={specName}>
                                        {specName}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Company</Label>
                            <Input 
                                type="select" 
                                value={user.company || ''} 
                                onChange={(e) => setUser({ ...user, company: e.target.value })} 
                            >
                                <option value="">-</option>
                                {COMPANY_OPTIONS.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                                {user.company && !COMPANY_OPTIONS.includes(user.company) && (
                                    <option value={user.company}>{user.company}</option>
                                )}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Manager</Label>
                            <Select
                                isClearable
                                isSearchable
                                name="manager"
                                value={selectedManagerOption}
                                onChange={(opt) => setUser({ ...user, manager: opt ? opt.value : '' })}
                                options={managerOptions}
                                placeholder="Select manager..."
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Hire Year</Label>
                            <Input 
                                type="number" 
                                placeholder="e.g. 2024"
                                value={user.hireYear || ''} 
                                onChange={(e) => setUser({ ...user, hireYear: e.target.value ? parseInt(e.target.value) : '' })} 
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Hire Month</Label>
                            <Input 
                                type="number" 
                                min="1"
                                max="12"
                                placeholder="1 - 12"
                                value={user.hireMonth || ''} 
                                onChange={(e) => setUser({ ...user, hireMonth: e.target.value ? parseInt(e.target.value) : '' })} 
                            />
                        </FormGroup>
                        <FormGroup check className="mt-2 mb-2">
                            <Label check>
                                <Input 
                                    type="checkbox" 
                                    id="userMustChangePasswordCheckbox"
                                    checked={!!user.mustChangePassword} 
                                    onChange={(e) => setUser({ ...user, mustChangePassword: e.target.checked })} 
                                />{' '}
                                {t('users.mustChangePassword') || 'Force password change at next login'}
                            </Label>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" type="submit">{t('volumes.save')}</Button>
                        <Button color="secondary" onClick={toggleUserModal}>{t('volumes.cancel')}</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <Modal isOpen={passwordModalOpen} toggle={togglePasswordModal}>
                <Form onSubmit={handleChangePassword}>
                    <ModalHeader toggle={togglePasswordModal}>
                        {t('users.changePassword')}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>{t('users.newPassword')}</Label>
                            <Input 
                                type="password" 
                                required
                                value={passwordData.newPassword} 
                                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} 
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('users.confirmPassword')}</Label>
                            <Input 
                                type="password" 
                                required
                                value={passwordData.confirmPassword} 
                                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} 
                            />
                        </FormGroup>
                        <FormGroup check className="mt-2 mb-2">
                            <Label check>
                                <Input 
                                    type="checkbox" 
                                    id="changePasswordMustChangeCheckbox"
                                    checked={!!passwordData.mustChangePassword} 
                                    onChange={(e) => setPasswordData({ ...passwordData, mustChangePassword: e.target.checked })} 
                                />{' '}
                                {t('users.mustChangePassword') || 'Force password change at next login'}
                            </Label>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" type="submit">{t('volumes.save')}</Button>
                        <Button color="secondary" onClick={togglePasswordModal}>{t('volumes.cancel')}</Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </Card>
    );
};

export default withTranslation(Users);
